<template>
  <ion-app :data-route-preview="isPreviewRoute">
    <transition name="splash-fade">
      <div v-if="showSplashScreen" class="splash-overlay" :key="splashScreenKey">
        <SplashScreen
          :isAnimation="true"
          :updating="otaGate.status.value !== 'idle'"
          :percent="otaGate.percent.value"
        />
      </div>
    </transition>
    <!-- The live update straight after sign-in; shown only while one downloads. -->
    <div v-if="otaSignIn.status.value !== 'idle'" class="splash-overlay">
      <SplashScreen :isAnimation="true" :updating="true" :percent="otaSignIn.percent.value" />
    </div>
    <TimeoutModal v-if="showTimeoutModal" :is-open="showTimeoutModal" @retry="handleTimeoutRetry" />
    <ForceUpdateModal
      v-if="showForceUpdateModal"
      :is-open="showForceUpdateModal"
      :message="forceUpdateMessage"
      :store-url="forceUpdateStoreUrl"
      :minimum-version="forceUpdateMinimumVersion"
      @update="handleForceUpdate"
    />
    <!--
      The force-update modal is non-dismissable and sits above the splash
      overlay, so nothing behind it is reachable anyway.
      Unmounting the outlet here while a page was mid-transition made Ionic
      fire that page's ionViewDidEnter on a torn-down instance at every
      force-update launch (issue #1815).
    -->
    <ion-router-outlet
      :animated="shouldAnimateRoute"
      :swipeGesture="swipeGestureEnabled"
      v-if="!showTimeoutModal"
    />
    <PaymentSheet />
    <!--
      Store toasts render wherever a <Toast> is on screen, and most pages mount
      none — so a message dispatched from a store module (a reward claimed on
      home, a failed fetch) was often never seen. This host shows it then, and
      stays quiet whenever a page-level host is on screen to show it instead.
    -->
    <Toast fallback />
    <!--
      The pending-reset notice (encryption spec 6.7, screen 7): "on every
      approved device, at app launch rather than on entering Next Steps,
      because the member has to see it". HERE, in the app shell, rather than on
      a Next Steps page, because the member it has to reach is the one who was
      not going to open Next Steps this week — a reset deletes everything they
      have written on a seven-day clock. Renders nothing at all until this
      device holds the key and the status says a hold is running.
    -->
    <ResetPendingNoticeHost />
    <!--
      The new-device request (encryption spec 6.7, screen 3). Once, here, and
      gated inside the host: a copy per launch page was two modals for one
      request (#1550; the host says why). Renders nothing until this device
      holds the key and somebody is waiting — and then on ANY route since
      #1691, because the member is standing there with the new phone and this
      is the only device that can answer. The two silences are the screens in
      `REQUEST_DEFERRING_PATHS` and the reset notice above.

      KEEP `ResetPendingNoticeHost` ABOVE THIS ONE. Both are app-shell modals
      over the same member, and the notice must win when a reset and a waiting
      device are due at the same launch. The host re-checks on `$nextTick` so
      the order is not load-bearing for correctness, but declaring the notice
      first is what makes it win the first flush rather than presenting and
      dismissing underneath — the stacked-modal shape #1550 was about.
    -->
    <PendingDeviceRequestHost />
  </ion-app>
</template>

<script>
import { useAuth0 } from '@/utils/auth0';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { RETURN_URL_PREFIX as HITPAY_RETURN_URL_PREFIX } from '@/utils/hitpay-checkout';

// Dev-only consoles reachable by deep link, e.g.
//   xcrun simctl openurl booted "app.mobile.hogc:///voucher-states"
// Matched by substring against the incoming URL, so each entry is both the test
// and the destination.
const DEV_DEEP_LINK_PATHS = [
  '/payment-demo',
  '/my-payments',
  '/payment-states',
  '/next-steps-mock',
  '/voucher-states',
  '/egiro-states',
  '/bf-pledge-states',
  '/agm-states',
  '/form-share-states',
  '/product-purchase',
];
import FadeTransition from './components/common/FadeTransition.vue';
import { IonApp, IonRouterOutlet, isPlatform, useBackButton, useIonRouter } from '@ionic/vue';
import { isNativeNotWeb } from '@/utils/platform-check';
import { computed, defineComponent, onMounted, ref, nextTick, watch } from 'vue';
import ONBOARDING_STATUS from '@/constants/ONBOARDING_STATUS';
import { createLogoutUri } from '@/utils/redirect-uri';
import {
  classifyDeepLink,
  clearPendingDeepLinkRoute,
  getPendingDeepLinkRoute,
  NFCCARD_FALLBACK_PATH,
  normalizePendingDeepLinkRoute,
  resolveDynamicDeepLink,
  savePendingDeepLinkRoute,
} from '@/utils/deeplink';
import { useRoute } from 'vue-router';
import { useOtaUpdateGate } from '@/composables/useOtaUpdateGate';
import { useOtaSignInUpdate } from '@/composables/useOtaSignInUpdate';
import { mapState } from 'vuex';
import SplashScreen from './components/SplashScreen.vue';
import TimeoutModal from './components/common/TimeoutModal.vue';
import ForceUpdateModal from './components/common/ForceUpdateModal.vue';
import PaymentSheet from './components/Payment/PaymentSheet.vue';
import Toast from './components/common/Toast.vue';
import { useVersionCheck } from './composables/useVersionCheck';
import { handOffToOs } from '@/utils/os-handoff';
import { emitAnnotationTelemetry } from '@/utils/courses-autosave/telemetry';
import { createPinSessionLifecycle } from '@/utils/giving-pin-session';
import { useFeatureFlags } from './composables/useFeatureFlags';
import { createMemberFlagsSync } from './utils/member-flags-sync';
import { syncAppIconWithFeatureFlags } from './utils/app-icon';
import {
  applyOtaKillSwitch,
  resignalOtaAppReady,
  syncOtaChannel,
  OTA_FEATURE_FLAG,
} from './utils/ota';
import router from './router';
import store from './store';
import { installSealedWrites } from '@/utils/courses-encryption/sealed-writes';
import { installPlaintextCacheGuard } from '@/utils/courses-encryption/plaintext-caches';
import { autosave, drainAutosaveOutbox, installAutosave } from '@/utils/courses-autosave';
import { installResetNoticeCheck } from '@/utils/courses-encryption/reset-notice';
import { installEnrolmentResumeCheck } from '@/utils/courses-encryption/enrolment-resume';
import { installPushTapRefresh } from '@/utils/courses-encryption/push-taps';
import { installKeyHydration } from '@/utils/courses-encryption/key-hydration';
import { installLinkRefresh } from '@/utils/member-split/link-refresh';
import { installStrandedStateCheck } from '@/utils/courses-encryption/stranded-state';
import { installOrphanedKeyRegistration } from '@/utils/courses-encryption/orphaned-key-registration';
import { installSignedOutWatcher } from '@/utils/courses-encryption/signed-out-watcher';
import { installRotationResume } from '@/utils/courses-encryption/rotation-resume';
import ResetPendingNoticeHost from '@/pages/courses/encryption/ResetPendingNoticeHost.vue';
import PendingDeviceRequestHost from '@/pages/courses/components/notes-hub/PendingDeviceRequestHost.vue';
import { isStoryblokPreviewMode } from './utils/storyblok';
import {
  HOMEPAGE_ACTION_NAMES,
  CRITICAL_API_INDICES,
  API_CALL_TIMEOUT,
  INITIALIZATION_TIMEOUT,
  withTimeout,
  processApiResults,
  verifyCriticalApis,
  checkStoreData,
  determineTargetPath,
  preserveRouteOrFallback,
  retryWithBackoff,
} from './utils/api';
import {
  AUTHENTICATED_HOME_PATH,
  normalizePostLoginTarget,
  replaceWithRootNavigation,
  resolvePostGateNavigation,
} from './utils/post-login-navigation';
import navigationService from '@/utils/navigation-service';
import { TRAINING_GATEWAY_PATHS } from '@/utils/training-gateway';

// Pages that run their own flow (the Training gateway signs in by itself): no boot navigation,
// so no onboarding or home redirect takes the visitor away.
const runsOwnFlow = path => path === '/youtube' || TRAINING_GATEWAY_PATHS.includes(path);
import {
  HARDWARE_BACK,
  isIonicSwipeBackEnabled,
  isRootExitPath,
  isTabRootPath,
  resolveHardwareBackAction,
} from '@/utils/android-back-navigation';

export default defineComponent({
  name: 'App',
  components: {
    IonApp,
    IonRouterOutlet,
    SplashScreen,
    TimeoutModal,
    ForceUpdateModal,
    FadeTransition,
    PaymentSheet,
    Toast,
    ResetPendingNoticeHost,
    PendingDeviceRequestHost,
  },
  data() {
    return {
      previousRoute: null,
    };
  },
  computed: {
    ...mapState('homepage', ['middleNavIcons', 'featuredCards', 'justForYou']),
    ...mapState('updates', ['unreadCount']),
    ...mapState('profile', ['hasRegistered']),
    ...mapState('churchUpdates', ['featuredChurchUpdates']),
    isPreviewRoute() {
      return typeof window !== 'undefined' && this.$route.path.startsWith('/preview/');
    },
    swipeGestureEnabled() {
      return (
        isIonicSwipeBackEnabled() &&
        !this.$route.matched.some(m => m.meta?.disableSwipeNavigation === true)
      );
    },
    shouldAnimateRoute() {
      // Only disable animation when navigating between tab routes
      // Tab routes: /tabs/home, /tabs/library, /tabs/all, /tabs/me
      const tabRoutes = ['/tabs/home', '/tabs/library', '/tabs/all', '/tabs/me'];
      const currentIsTab = tabRoutes.some(
        path => this.$route.path === path || this.$route.path.startsWith(path + '/'),
      );
      const previousIsTab = tabRoutes.some(
        path =>
          this.previousRoute?.path === path || this.previousRoute?.path?.startsWith(path + '/'),
      );

      // Disable animation only when navigating between tabs
      return !(currentIsTab && previousIsTab);
    },
  },
  watch: {
    $route(to, from) {
      // Store previous route for animation decision
      this.previousRoute = from;
    },
  },
  setup() {
    const route = useRoute();
    const ionRouter = useIonRouter();
    const navigateAfterAuthenticatedBootstrap = targetPath => {
      const normalizedTargetPath = normalizePostLoginTarget(targetPath);

      if (
        normalizedTargetPath === AUTHENTICATED_HOME_PATH ||
        normalizedTargetPath === '/register'
      ) {
        return replaceWithRootNavigation(router, ionRouter, normalizedTargetPath);
      }

      return router.push(normalizedTargetPath);
    };
    const completePostGateNavigation = async (navigation, shouldConsumePendingDeepLink) => {
      await navigation;
      if (shouldConsumePendingDeepLink) {
        clearPendingDeepLinkRoute();
      }
    };
    const getPostGateNavigationTarget = (fallbackTarget, onboardStatusSuccess = true) => {
      const pendingDeepLinkRoute = getPendingDeepLinkRoute();
      return resolvePostGateNavigation({
        hasRegistered: onboardStatusSuccess
          ? store.state.profile.hasRegistered
          : ONBOARDING_STATUS.STAGE_3_LIFESTAGE_COMPLETED,
        onboardingCompleteStatus: ONBOARDING_STATUS.STAGE_3_LIFESTAGE_COMPLETED,
        pendingDeepLinkRoute,
        fallbackTarget,
      });
    };

    const isPreview =
      typeof window !== 'undefined' &&
      (route.path.startsWith('/preview/') || isStoryblokPreviewMode());

    const splashManuallyDismissed = ref(false);
    const otaGate = useOtaUpdateGate();
    const otaSignIn = useOtaSignInUpdate();

    const showSplashScreen = computed(() => {
      return !splashManuallyDismissed.value && !isPreview;
    });

    const isInitializing = ref(false);
    const showTimeoutModal = ref(false);
    const splashScreenKey = ref(0);
    // The hostname clause is for a developer running `yarn serve` in a browser.
    // It must NOT match the packaged app: Capacitor serves the webview from
    // `capacitor://localhost`, so `hostname` is literally 'localhost' on every
    // native build — which turned a local-testing escape hatch into permanent
    // production behaviour. With it on, a bootstrap failure skips the timeout
    // modal and dismisses the splash after 50ms, so a member on a bad network is
    // told nothing at all.
    const disableTimeoutReloadForLocal =
      process.env.VUE_APP_DISABLE_TIMEOUT_RELOAD === 'true' ||
      ['local', 'development'].includes(process.env.VUE_APP_NODE_ENV) ||
      (!isNativeNotWeb() &&
        typeof window !== 'undefined' &&
        ['localhost', '127.0.0.1'].includes(window.location.hostname));

    const continueWithoutTimeoutModal = async reason => {
      console.warn(
        `[App] Timeout/reload modal bypassed for local testing (${reason}); continuing app flow`,
      );
      const excludedPaths = ['/', '/pre-sign-in', '/login', '/pre-loading'];
      const targetPath = route.path && !excludedPaths.includes(route.path) ? route.path : '/home';
      await router.push(targetPath);
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 50));
      await dismissSplashAfterVersionCheck();
    };

    // Load feature flags (non-blocking)
    const { loadAppFlags, loadUserFlags, hasMemberFlags, isEnabled, getFlag } = useFeatureFlags();
    const syncConfiguredAppIcon = () => {
      syncAppIconWithFeatureFlags(isEnabled).catch(() => {});
    };
    // Once per signed-in member, and again after a failed load: see @/utils/member-flags-sync.
    const memberFlags = createMemberFlagsSync({
      loadUserFlags,
      hasMemberFlags,
      onSettled: syncConfiguredAppIcon,
    });
    const loadUserFlagsAndSyncAppIcon = token => {
      memberFlags.sync(token);
    };

    const appFlagsLoaded = loadAppFlags().catch(() => {});

    // Subscribing to an update channel is what makes the backend offer this
    // device a bundle, so doing it only once there is a token is the gate on
    // downloading anything at all. A signed-out install stays unassigned and is
    // offered nothing. Deliberately not in main.js, which runs before sign-in.
    //
    // This leans on the Capgo project default channel serving no bundle — an
    // unassigned device falls back to it. See src/utils/ota.js.
    // Resolve the kill switch BEFORE anything tries to assign a channel.
    //
    // `.finally()` runs callbacks in registration order, and the token watcher
    // below is `immediate: true` — so with the two the other way round, a device
    // recovering from a kill declined here on a marker that the very next
    // callback cleared. Recovery cost three launches: one to clear the marker,
    // one to assign, one to download, with nothing saying why. During an
    // incident that reads as "the flag did not work".
    //
    // Registering first costs nothing and does not widen what lifts the block:
    // applyOtaKillSwitch already clears the marker on any `true`, so the same
    // values lift it — one launch sooner.
    appFlagsLoaded.finally(() => {
      // getFlag, not isEnabled: the kill switch must act only on an explicit
      // false, so an outage of the flags API cannot revert every install.
      applyOtaKillSwitch(getFlag(OTA_FEATURE_FLAG));
    });

    watch(
      () => store.state.user?.token,
      token => {
        if (!token) return;
        // Waits for the flags, because assignment is the download gate and an
        // absent or false `ota_enabled` has to leave the device unassigned.
        // Firing before they land would subscribe the device for the one launch
        // that matters — the first one on a new binary.
        //
        // A NEW assignment (a first sign-in on this install or binary) also
        // checks for an update right away. The plugin's own check ran at launch,
        // before the device had a channel, so without this a new member needs
        // two more launches to get the channel's bundle. The page is saved so
        // the restart comes back to it. See src/utils/ota-sign-in-update.js.
        appFlagsLoaded.finally(async () => {
          const assigned = await syncOtaChannel(getFlag(OTA_FEATURE_FLAG));
          if (!assigned) return;
          otaSignIn
            .run(() =>
              savePendingDeepLinkRoute({ path: route.path, query: route.query, hash: route.hash }),
            )
            .catch(error => console.warn('[OTA] Sign-in update failed:', error?.message || error));
        });
      },
      { immediate: true },
    );

    appFlagsLoaded.finally(() => {
      // The kill switch is handled above, ahead of the token watcher.
      if (store.state.user?.token) {
        loadUserFlagsAndSyncAppIcon(store.state.user.token);
        return;
      }

      syncConfiguredAppIcon();
    });

    // Load user-specific flags whenever a member signs in, not only the first time: on native a
    // sign-out keeps this document, so the next member on the phone signs in through here too.
    watch(
      () => store.state.user?.token,
      token => {
        appFlagsLoaded.finally(() => {
          loadUserFlagsAndSyncAppIcon(token);
        });
      },
    );

    // Seal every Next Steps annotation on its way out (spec section 6.7): the
    // request hook of the annotations transport seam. Installed here, at
    // start-up, rather than at enrolment, because after a reload the hook has
    // to be in place before the first write of the session — and it is a
    // byte-for-byte pass-through until `courses-keys` holds a key, so a member
    // who never enrols writes exactly what they wrote before.
    installSealedWrites({ store });

    // And stop the courses store's localStorage read-caches from holding an
    // enrolled member's notes in the clear (spec section 6.7): the read path
    // opens envelopes at the seam, so what the store caches is plaintext. The
    // guard flips `courses.encryptedMember` as enrolment becomes known and
    // sweeps what the caches held from before. Part 2 of #1548 replaces the
    // caches with the IndexedDB corpus.
    installPlaintextCacheGuard({ store });
    // Bind the autosave engine to the signed-in member, so an edit one member
    // left in the queue on a shared phone can never be sent under the next
    // member's token.
    installAutosave({ store });

    const drainOutboxNow = () =>
      drainAutosaveOutbox({
        store,
        memberId: store.state?.profile?.id ?? null,
        // Every generation this device can OPEN a row under — both, while a
        // rotation is running. An entry sealed under a `kid` that is not in
        // this list can never be opened again, so the drain drops it rather
        // than storing something nobody could read.
        keyIds: (store.getters['courses-keys/readGenerations'] || []).map(g => g.keyId),
      }).catch(() => {});

    // The network coming back. A HINT, not a dependency: every retryable row
    // already has a backoff timer and will try itself within thirty seconds, so
    // this only makes it sooner — and `navigator.onLine` reports whether an
    // interface exists, not whether the API is reachable, so it says "online"
    // behind a captive portal too. The authority on connectivity stays a request
    // that failed. That is also why no native connectivity plugin was added:
    // there is nothing here for one to be more right about.
    window.addEventListener('online', () => {
      autosave.retryNow();
      drainOutboxNow();
    });

    // Anything an earlier session could not send. Not awaited: the drain puts
    // the pending bodies back on screen itself, and holding boot on a network
    // call would be the wrong trade for a queue that is empty in the ordinary
    // case. It waits for the key store first — an entry is an envelope, and it
    // cannot be shown without one.
    store
      .dispatch('courses-keys/ready')
      .catch(() => {})
      .finally(drainOutboxNow);

    // And again the moment this device comes to hold the key mid-session —
    // approved from another of the member's phones, or its own enrolment
    // finishing. That is what makes an entry queued before enrolment sendable
    // (the drain re-seals it), and nothing else would drain until the next
    // resume; the phone the member just approved should not have to be
    // backgrounded before its words go up.
    watch(
      () => store.getters['courses-keys/hasMasterKey'] === true,
      holdsKey => {
        if (holdsKey) drainOutboxNow();
      },
    );

    // And read the member's encryption status once, at launch, on a device that
    // holds their notes key — which is the only way the pending-reset notice
    // above can reach a member who never opens Next Steps (spec section 6.7,
    // screen 7; reset-notice.js says why the key is the trigger). One request
    // per launch for an enrolled member, none at all for anybody else.
    installResetNoticeCheck({ store });

    // And reconcile an enrolment attempt this device sent but never heard
    // back from (spec section 6.7, decision 40): the member may already be
    // enrolled under a key only this phone's attempt row knows, and every
    // gate on the ordinary enrolment path can legitimately be closed by then.
    // enrolment-resume.js says why nothing else would ever ask.
    installEnrolmentResumeCheck({ store });

    // A tap on one of the two Next Steps pushes reads the status again, so the
    // prompt the member opened the notification for is decided from what is
    // true now rather than from the launch snapshot (#1692). Navigation needs
    // nothing: both hosts are mounted here and present on any route.
    installPushTapRefresh({ store });

    // And notice a waiting login's link to its member without a sign-out: it
    // re-reads the profile on resume, and the member id landing is what the
    // key hydration below keys on. Installed first, so the login's devices
    // snapshot is dropped before the member's keys are hydrated.
    // link-refresh.js says why.
    const linkRefresh = installLinkRefresh({
      store,
      onLinked: () => {
        loadUserFlags(store.state.user?.token)
          .catch(() => {})
          .finally(syncConfiguredAppIcon);
      },
    });

    // And hydrate the member's Next Steps key material as soon as we know who
    // they are (spec section 6.2, startup order) — asking for the profile
    // ourselves when a token is here before the id is, rather than waiting on
    // whichever page fetches it first. key-hydration.js says why.
    installKeyHydration({ store });

    // And say so, loudly and once, if a member is ever stranded: no key on
    // this phone, enrolled, no other approved device and no recovery code
    // (#1652), a shape the enrolment rules make unreachable — reported wherever the member is,
    // not only if they reach the approval screen. stranded-state.js says why.
    installStrandedStateCheck({ store });

    // And put right the shape that MAKES a member stranded: a phone holding
    // the key with no device row, which is what sign-out and back in leaves on
    // a platform whose copy survives. It reads as perfectly healthy and has
    // silently stopped being able to approve anything.
    // orphaned-key-registration.js says how it happens and why nothing caught it.
    installOrphanedKeyRegistration({ store });

    // And make a sign-out bite on the phone that was signed out, on the status
    // read that says so — not on its next launch. signed-out-watcher.js says
    // what a phone did between the two until now.
    installSignedOutWatcher({ store });

    // And finish a rotation this device started and was interrupted in, from
    // wherever the member relaunches — until now only the Sign out screen
    // could, and nothing led back to it. rotation-resume.js says what that
    // left a member looking at.
    installRotationResume({ store });

    const dismissSplashScreen = () => {
      console.log('[App] dismissSplashScreen called');
      splashManuallyDismissed.value = true;
    };

    useBackButton(10, () => {
      const current = router.currentRoute.value;
      const action = resolveHardwareBackAction({
        disableBack: current.matched.some(m => m.meta?.disableSwipeNavigation === true),
        isOnTabRoute: isTabRootPath(current.path),
        hasCustomHandler: navigationService.hasCustomHandler(),
        canGoBack: ionRouter.canGoBack(),
        isRootRoute: isRootExitPath(current.path),
      });

      switch (action) {
        case HARDWARE_BACK.IGNORE:
          if (window.navigator?.vibrate) {
            window.navigator.vibrate(50);
          }
          return;
        case HARDWARE_BACK.EXIT:
          CapApp.exitApp();
          return;
        case HARDWARE_BACK.CUSTOM:
          navigationService.goBack(ionRouter);
          return;
        default:
          ionRouter.back();
      }
    });

    const auth0 = useAuth0();
    const {
      handleRedirectCallback,
      getAccessTokenSilently,
      isAuthenticated: auth0IsAuthenticated,
      logout,
    } = auth0;
    const _isLocalMode = process.env.VUE_APP_NODE_ENV === 'local';
    // In local mode, Auth0's isAuthenticated is always false (no real session).
    // Treat localStorage token as proof of authentication instead.
    const isAuthenticated = computed(() =>
      _isLocalMode ? !!localStorage.getItem('token') : auth0IsAuthenticated.value,
    );
    const isNative = isPlatform('android') || isPlatform('ios');
    const isNativeBrowser = isPlatform('mobileweb');

    const handleClassifiedDeepLink = async openedUrl => {
      const navigateToDeepLinkTarget = async targetRoute => {
        const pendingRoute = savePendingDeepLinkRoute(targetRoute);
        if (!pendingRoute || !isAuthenticated.value) {
          await router.push(targetRoute);
          return;
        }

        const targetDecision = resolvePostGateNavigation({
          hasRegistered: store.state.profile.hasRegistered,
          onboardingCompleteStatus: ONBOARDING_STATUS.STAGE_3_LIFESTAGE_COMPLETED,
          pendingDeepLinkRoute: pendingRoute,
          fallbackTarget: targetRoute,
        });

        await completePostGateNavigation(
          navigateAfterAuthenticatedBootstrap(targetDecision.target),
          targetDecision.shouldConsumePendingDeepLink,
        );
      };

      const deepLinkAction = await classifyDeepLink(openedUrl);
      if (deepLinkAction.type === 'home') {
        router.push({ path: NFCCARD_FALLBACK_PATH });
        return true;
      }
      if (deepLinkAction.type === 'external') {
        // handOffToOs, NOT openExternal: only handing the URL to the OS lets the
        // destination's own universal link open its target app. `openExternal` goes
        // through `window.open(url, '_blank')`, which is served in an in-app browser
        // — it showed bible.com as a web page and NSL2-3's reading plan never
        // reached YouVersion. Same call and same reasoning as the resource gateway,
        // which had the identical bug in its own copy of this. See utils/os-handoff.js.
        //
        // Still flag-gated. This lane has never functioned in production — the flag
        // has no `feature_flag` row, so it reads as off everywhere — and the value of
        // keeping the switch is that the hand-off's behaviour is device-specific:
        // if some handset misbehaves, the row is a kill switch that needs no deploy
        // and no store release.
        if (isEnabled('deeplink-external')) {
          await handOffToOs(deepLinkAction.externalUrl, { source: 'deeplink' });
        }
        // Land the app on Home behind the hand-off so dismissing the browser (or a
        // flag-off / invalid-url build) is never a dead screen — matches unknown-slug.
        router.push({ path: NFCCARD_FALLBACK_PATH });
        return true;
      }
      if (deepLinkAction.type === 'direct') {
        await navigateToDeepLinkTarget(deepLinkAction.route);
        return true;
      }
      if (deepLinkAction.type === 'dynamic') {
        const targetRoute = await resolveDynamicDeepLink(deepLinkAction);
        if (!targetRoute) {
          router.push({ path: NFCCARD_FALLBACK_PATH });
          return true;
        }
        await navigateToDeepLinkTarget(targetRoute);
        return true;
      }
      return false;
    };

    // MyGiving PIN session across background/foreground (@/utils/giving-pin-session).
    const pinSessionLifecycle = createPinSessionLifecycle({ store, router });

    CapApp.addListener('pause', () => {
      // The MyGiving PIN session is NOT ended here: a member paying by eGIRO or
      // switching to their banking app is mid-transaction. It ends on the way home
      // (router afterEach) or after the server's idle window (resume, below).
      pinSessionLifecycle.onPause();
      // Everything unsent goes to disk, and NOTHING is sent. iOS gives about
      // five seconds here before it freezes the app, and it can only kill a
      // frozen one — so a request started now is a race against that, while a
      // Preferences write is milliseconds and wins it. This is the whole answer
      // to the camera-swap case (plan §3.3).
      autosave.pause().catch(() => {});
      // The annotation write path's per-session summary (plan §8.5). `pause` and
      // logout are the two moments a session reliably ends; the RUM SDK batches,
      // so this may not leave the device until the next resume, which is fine.
      // A session that never opened Next Steps counted nothing and emits nothing.
      emitAnnotationTelemetry('pause');
    });

    // The updater re-waits for the ready signal after every foreground check;
    // without this each warm resume on iOS stalled that wait for the full
    // appReadyTimeout. Not awaited — see resignalOtaAppReady in @/utils/ota.
    CapApp.addListener('resume', () => {
      resignalOtaAppReady();
      pinSessionLifecycle.onResume();
      // The member's flags, if every retry of a failed load ran out while the app was open.
      memberFlags.retryIfMissing();
      // A login waiting for its leader's link may have been linked meanwhile.
      linkRefresh.refresh();
      // The Next Steps key-sync eligibility can change while the app is in the
      // background (iCloud Keychain toggled, a screen lock set), and the nudge
      // reads it; re-probe on every return to the foreground.
      store.dispatch('courses-keys/probe').catch(() => {});
      // Back in the foreground: send what `pause` put on disk, and give any row
      // that had run out of attempts a fresh set.
      autosave.retryNow();
      drainOutboxNow();
      // Then re-read whatever the member has open, because nothing pushes
      // another device's write to a screen that already holds the row: a note
      // edited on their tablet while this phone was in the background is
      // otherwise invisible until they type on it and lose the race. AFTER the
      // drain, so a body this device has not managed to send yet is still the
      // outbox's and is skipped rather than painted over. The store decides what
      // is open; this knows nothing about which pages are mounted.
      store.dispatch('courses/refreshOpenAnnotations').catch(() => {});
      // And so can the member's devices: a sign-out from another phone lands
      // while this one is in the background, and the read is what lets the
      // signed-out watcher act on it. Only on a device holding the key — the
      // one a sign-out is about; a keyless one polls from its own screen.
      if (store.getters['courses-keys/hasMasterKey'] === true) {
        store.dispatch('courses-devices/refreshStatus').catch(() => {});
      }
    });

    CapApp.addListener('appUrlOpen', async ({ url }) => {
      console.log('before capapp');

      // The HitPay checkout owns this callback and closes the browser itself
      // (see @/utils/hitpay-checkout). Leave it alone: the URL's host is
      // `payment-return` with an EMPTY path, so classifyDeepLink calls it 'home'
      // and the fallthrough below would push the member to /home mid-settle,
      // straight out of the course they were paying for.
      if (url?.startsWith(HITPAY_RETURN_URL_PREFIX)) return;
      // Dev-only: deep-link straight to payment screens for QA, e.g.
      //   xcrun simctl openurl booted "app.mobile.hogc:///payment-demo"
      //   xcrun simctl openurl booted "app.mobile.hogc:///my-payments"
      if (process.env.VUE_APP_NODE_ENV !== 'production') {
        // `/payment-details/` first: it carries a reference in the path, so the
        // plain substring list below cannot match it.
        if (url.includes('/payment-details/')) {
          const ref = url.split('/payment-details/')[1]?.split(/[?#]/)[0];
          if (ref) {
            router.push({ path: `/payment-details/${ref}` });
            return;
          }
        }
        // Each of these is a console with a route and NO UI entry point, so on a
        // simulator the deep link is the only way in — the state harnesses most
        // of all, since they exist precisely because their states are hard to
        // reproduce against a real backend. This was eight copies of the same
        // three lines; adding a console should be a list entry, not a branch.
        //
        // '/egiro-states' is matched here, before the '/e-giro/create' bank
        // callback below. The two do not overlap.
        const harness = DEV_DEEP_LINK_PATHS.find(path => url.includes(path));
        if (harness) {
          router.push({ path: harness });
          return;
        }
      }
      if (url.includes('/e-giro/create')) {
        if (!(await canNavigateAfterForceUpdateCheck('eGIRO create deep link'))) {
          return;
        }
        const isFromRecurringGiving = sessionStorage.getItem('isFromRecurringGiving') === 'true';
        sessionStorage.removeItem('isFromRecurringGiving');
        router.push({
          path: '/my-giving/application-in-progress',
          query: isFromRecurringGiving ? { fromrecurring: 'true' } : {},
        });
        return;
      } else if (url.includes('/e-giro/cancel')) {
        if (!(await canNavigateAfterForceUpdateCheck('eGIRO cancel deep link'))) {
          return;
        }
        router.push({ path: '/my-giving/remove-bank' });
        return;
      }

      // Handle Auth0 callback/logout before deep link classification
      // These URLs come from mobile.* hosts and would otherwise be classified as 'direct'
      const isAuthCallback =
        url.includes('state') && (url.includes('code') || url.includes('error'));
      if (isAuthCallback || url.includes('/callback') || url.includes('/logout')) {
        let appState = null;
        if (isAuthCallback) {
          try {
            const redirect = await handleRedirectCallback(url);
            appState = redirect.appState;
            console.log('appState', appState);
          } catch (e) {
            // Was console.log, so a failed code exchange never reached Datadog.
            // A WebView the OS reclaims while the system browser is up loses the
            // sessionStorage holding state/code_verifier, and this throws
            // "Invalid state" — no tokens are stored and every later launch
            // reports missing_refresh_token. Logging it is what makes that
            // hypothesis testable (#1981).
            console.warn('[Auth0] redirect callback failed:', e?.error || e?.message, e);
          }
          console.log('after redirect capapp');
        }

        try {
          await Browser.close();
        } catch (error) {
          const isAlreadyClosed =
            error?.message?.includes('No active window') ||
            error?.message?.includes('No active window to close');
          console.log(
            isAlreadyClosed
              ? '[App] Browser already closed, ignoring error'
              : '[App] Browser close error:',
            error,
          );
        }

        if (url.includes('/callback')) {
          console.log('login done...');
          if (isNative && !isNativeBrowser) {
            let resumeRoute = normalizePendingDeepLinkRoute(appState?.fullPathObj);
            if (!resumeRoute) {
              resumeRoute = getPendingDeepLinkRoute();
            }

            if (resumeRoute) {
              savePendingDeepLinkRoute(resumeRoute);
            }
          }
        } else if (url.includes('/logout')) {
          console.log('logout done...');

          const { clearAnalyticsSessions } = await import('@/utils/clear-analytics-sessions');
          await clearAnalyticsSessions();

          window.location.href = '/';
        }
        return;
      }

      if (!(await canNavigateAfterForceUpdateCheck('deep link'))) {
        return;
      }
      if (await handleClassifiedDeepLink(url)) {
        return;
      }
    });

    console.log('Capapp: ' + isAuthenticated.value);

    const setupCrispCloseListener = () => {
      if (window.$crisp) {
        try {
          window.$crisp.push([
            'on',
            'chat:closed',
            () => {
              if (window.$crisp) {
                window.$crisp.push(['do', 'chat:hide']);
              }
              if (!isAuthenticated.value) {
                const currentPath = router.currentRoute.value.path;
                const excludePaths = [
                  '/pre-sign-in',
                  '/login',
                  '/registration',
                  '/forgot-password',
                  '/forgot-email',
                  '/forgot-email/reset',
                  '/forgot-email/contact-us',
                  '/',
                  '/privacy-policy',
                  '/terms-of-use',
                  '/success-launch',
                  '/youtube',
                  ...TRAINING_GATEWAY_PATHS,
                ];

                if (!excludePaths.includes(currentPath) && !currentPath.startsWith('/preview/')) {
                  console.log(
                    '[App] Crisp closed, user not authenticated, redirecting to pre-sign-in',
                  );
                  router.push('/pre-sign-in');
                }
              }
            },
          ]);
          console.log('[App] Crisp chat:closed listener registered');
        } catch (error) {
          console.error('[App] Error setting up Crisp close listener:', error);
        }
      } else {
        setTimeout(setupCrispCloseListener, 500);
      }
    };

    const setupCrispHideOnLoad = () => {
      if (window.$crisp) {
        try {
          window.$crisp.push(['do', 'chat:hide']);
          console.log('[App] Crisp chat hidden globally');
        } catch (error) {
          console.error('[App] Error hiding Crisp chat:', error);
        }
      } else {
        setTimeout(setupCrispHideOnLoad, 500);
      }
    };

    if (typeof window !== 'undefined') {
      const originalCrispReadyTrigger = window.CRISP_READY_TRIGGER;
      window.CRISP_READY_TRIGGER = function () {
        if (originalCrispReadyTrigger) {
          originalCrispReadyTrigger();
        }
        if (window.$crisp) {
          try {
            window.$crisp.push(['do', 'chat:hide']);
            console.log('[App] Crisp chat hidden via CRISP_READY_TRIGGER');
          } catch (error) {
            console.error('[App] Error hiding Crisp chat via CRISP_READY_TRIGGER:', error);
          }
        }
      };
    }

    onMounted(async () => {
      // index.html paints the body brand yellow so the native launch screen hands
      // off to the splash with no cream frame. The splash overlay now covers the
      // screen, so hand the body back to main.scss's colour underneath it.
      document.body.style.removeProperty('background-color');

      if (isInitializing.value) {
        console.warn('[App] Already initializing, skipping duplicate mount');
        return;
      }
      isInitializing.value = true;

      await nextTick();

      (async () => {
        try {
          if (runsOwnFlow(route.path)) {
            await dismissSplashAfterVersionCheck();
            return;
          }

          if (isPreview) {
            await dismissSplashAfterVersionCheck();
            if (route.path.startsWith('/preview/church-updates')) {
              store.dispatch('churchUpdates/getChurchUpdates').catch(err => {
                console.warn('[Preview] Error loading church updates:', err);
              });
            }
            return;
          }

          // In local mode, skip Auth0 readiness check (stub is always ready)
          if (!_isLocalMode) {
            const authInitStartTime = Date.now();
            let authReady = false;
            const authCheckTimeout = setTimeout(() => {
              const authInitDuration = Date.now() - authInitStartTime;
              console.warn(
                `[App] Auth0 initialization timeout (3s) - proceeding anyway. Duration: ${authInitDuration}ms`,
              );
              authReady = true;
            }, 3000);

            while (!authReady && (!auth0 || typeof auth0.isAuthenticated === 'undefined')) {
              await new Promise(resolve => setTimeout(resolve, 100));
              if (auth0 && typeof auth0.isAuthenticated !== 'undefined') {
                authReady = true;
              }
            }
            clearTimeout(authCheckTimeout);
          }

          if (isAuthenticated.value) {
            const initStartTime = Date.now();
            // Start version check in parallel with API calls to reduce total splash time
            const versionCheckPromise = performVersionCheck();

            let timeoutId = null;
            const timeoutPromise = new Promise(resolve => {
              timeoutId = setTimeout(() => {
                console.warn(
                  `[App] Initialization timeout (${
                    INITIALIZATION_TIMEOUT / 1000
                  }s) - showing timeout modal`,
                );
                resolve({ timeout: true, onboardStatusSuccess: false });
              }, INITIALIZATION_TIMEOUT);
            });

            const initPromise = (async () => {
              let onboardStatusSuccess = false;
              const onboardStartTime = Date.now();
              try {
                await store.dispatch('profile/getOnboardStatus', {
                  useCache: true,
                  backgroundSync: true,
                });
                console.log(
                  `[App] getOnboardStatus completed (non-blocking) in ${
                    Date.now() - onboardStartTime
                  }ms`,
                );
                onboardStatusSuccess = true;
              } catch (onboardError) {
                if (onboardError.response?.status === 401) {
                  console.error('[App] getOnboardStatus failed with 401 (Unauthorized)');
                  throw onboardError;
                }
                console.warn(
                  '[App] getOnboardStatus failed, proceeding assuming user is registered:',
                  {
                    error: onboardError.message,
                    status: onboardError.response?.status,
                    statusText: onboardError.response?.statusText,
                  },
                );
              }

              const actionNames = HOMEPAGE_ACTION_NAMES;
              const dispatchFunctions = [
                () => store.dispatch('homepage/getMiddleNavIcons'),
                () => store.dispatch('homepage/getFeaturedCards'),
                () => store.dispatch('updates/getUpdates', 'all'),
                () => store.dispatch('homepage/getAllAnimations'),
              ];

              // Start API calls - store actions synchronously populate from cache before first await
              const apiCallStartTimes = actionNames.map(() => Date.now());
              const apiPromises = dispatchFunctions.map((fn, i) =>
                withTimeout(fn(), API_CALL_TIMEOUT, actionNames[i]),
              );

              // Check if critical data is already in store from cache (populated synchronously above)
              const cachedData = checkStoreData(store);
              if (
                cachedData.hasMiddleNavIcons &&
                cachedData.hasFeaturedCards &&
                cachedData.hasAnimations &&
                cachedData.hasOnboardStatus
              ) {
                console.log(
                  '[App] Cached data found - navigating immediately, APIs refreshing in background',
                );
                // Let API calls refresh in background without blocking navigation
                Promise.allSettled(apiPromises).then(results => {
                  processApiResults(results, actionNames, apiCallStartTimes, store, false);
                });
                return { timeout: false, onboardStatusSuccess, failedCalls: [] };
              }

              // No cache - wait for API calls to complete
              const dataLoadResults = await Promise.allSettled(apiPromises);

              const { failedCalls, has401Error } = processApiResults(
                dataLoadResults,
                actionNames,
                apiCallStartTimes,
                store,
                true,
              );

              if (has401Error) {
                throw new Error('Unauthorized - one or more API calls failed with 401');
              }

              const criticalCheck = verifyCriticalApis(dataLoadResults, CRITICAL_API_INDICES);
              if (criticalCheck.shouldThrow) {
                const criticalResults = CRITICAL_API_INDICES.map(idx => ({
                  index: idx,
                  actionName: actionNames[idx],
                  status: dataLoadResults[idx]?.status || 'unknown',
                  error: dataLoadResults[idx]?.reason?.message || dataLoadResults[idx]?.reason,
                }));
                console.warn('[App] Critical APIs failed:', criticalResults);
                throw criticalCheck.error;
              }

              checkStoreData(store);

              return { timeout: false, onboardStatusSuccess, failedCalls };
            })();

            try {
              const result = await Promise.race([initPromise, timeoutPromise]);

              if (timeoutId && !result.timeout) {
                clearTimeout(timeoutId);
              }

              if (result.timeout) {
                const timeoutDuration = Date.now() - initStartTime;
                const apiResults = result.failedCalls || [];
                const storeData = checkStoreData(store);
                console.warn('[App] ⚠️ TIMEOUT MODAL TRIGGERED - Initialization timeout', {
                  duration: `${timeoutDuration}ms`,
                  timeoutLimit: `${INITIALIZATION_TIMEOUT}ms`,
                  reason: 'Overall initialization exceeded timeout limit',
                  failedApiCalls: apiResults.map(c => c.actionName),
                  storeData: {
                    hasMiddleNavIcons: storeData.hasMiddleNavIcons,
                    hasFeaturedCards: storeData.hasFeaturedCards,
                    hasAnimations: storeData.hasAnimations,
                    hasOnboardStatus: storeData.hasOnboardStatus,
                  },
                  timestamp: new Date().toISOString(),
                });
                if (disableTimeoutReloadForLocal) {
                  isInitializing.value = false;
                  await continueWithoutTimeoutModal('initialization-timeout');
                  return;
                }
                showTimeoutModal.value = true;
                isInitializing.value = false;
                return;
              }

              const { onboardStatusSuccess, failedCalls = [] } = result;
              const totalInitDuration = Date.now() - initStartTime;
              console.log(`[App] Initialization completed successfully in ${totalInitDuration}ms`);

              // Ensure splash animation fully completes (~1300ms) before dismissing
              const MIN_SPLASH_DURATION = 1500;
              const remainingTime = MIN_SPLASH_DURATION - totalInitDuration;
              if (remainingTime > 0) {
                await new Promise(resolve => setTimeout(resolve, remainingTime));
              }

              if (failedCalls.length > 0) {
                console.log(
                  `[App] ${failedCalls.length} API calls failed, will retry in background:`,
                  failedCalls.map(c => c.actionName),
                );
              }

              if (failedCalls.length > 0) {
                setTimeout(() => {
                  console.log('[App] Starting background retry for failed API calls...');
                  failedCalls.forEach(({ actionName, dispatch }) => {
                    dispatch()
                      .then(() => console.log(`[App] Background retry succeeded for ${actionName}`))
                      .catch(err =>
                        console.warn(`[App] Background retry failed for ${actionName}:`, err),
                      );
                  });
                }, 100);
              }

              const fallbackTarget = determineTargetPath(
                onboardStatusSuccess,
                store,
                route,
                ONBOARDING_STATUS.STAGE_3_LIFESTAGE_COMPLETED,
              );
              const targetDecision = getPostGateNavigationTarget(
                fallbackTarget,
                onboardStatusSuccess,
              );

              if (
                targetDecision.shouldConsumePendingDeepLink &&
                !(await canNavigateAfterForceUpdateCheck('post-bootstrap deep link'))
              ) {
                return;
              }
              await completePostGateNavigation(
                navigateAfterAuthenticatedBootstrap(targetDecision.target),
                targetDecision.shouldConsumePendingDeepLink,
              );
              await nextTick();
              await new Promise(resolve => setTimeout(resolve, 50));

              await dismissSplashAfterVersionCheck(versionCheckPromise);
            } catch (error) {
              console.error('[App] Error loading data:', {
                error: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                url: error.config?.url,
                stack: error.stack,
              });
              if (error.response && error.response.status === 401) {
                const { clearAnalyticsSessions } = await import('@/utils/clear-analytics-sessions');
                await clearAnalyticsSessions({ preserveAnalyticsIdentity: true });
                await logout({
                  logoutParams: {
                    returnTo: createLogoutUri(
                      process.env.VUE_APP_PACKAGE_ID,
                      process.env.VUE_APP_AUTH0_DOMAIN,
                    ),
                  },
                  openUrl: async url =>
                    await Browser.open({
                      url,
                      windowName: '_self',
                    }),
                });
                await dismissSplashAfterVersionCheck(versionCheckPromise);
              } else if (error.message && error.message.includes('No critical APIs succeeded')) {
                const errorDuration = Date.now() - initStartTime;
                const storeData = checkStoreData(store);
                console.warn('[App] ⚠️ TIMEOUT MODAL TRIGGERED - No critical APIs succeeded', {
                  duration: `${errorDuration}ms`,
                  reason:
                    'All critical APIs failed (getMiddleNavIcons, getFeaturedCards, getAllAnimations)',
                  storeData: {
                    hasMiddleNavIcons: storeData.hasMiddleNavIcons,
                    hasFeaturedCards: storeData.hasFeaturedCards,
                    hasAnimations: storeData.hasAnimations,
                    hasOnboardStatus: storeData.hasOnboardStatus,
                  },
                  timestamp: new Date().toISOString(),
                });
                if (disableTimeoutReloadForLocal) {
                  isInitializing.value = false;
                  await continueWithoutTimeoutModal('no-critical-apis');
                  return;
                }
                showTimeoutModal.value = true;
                isInitializing.value = false;
                return;
              } else {
                const targetPath = preserveRouteOrFallback(route, '/tabs/home');
                await navigateAfterAuthenticatedBootstrap(targetPath);
                await nextTick();
                await new Promise(resolve => setTimeout(resolve, 50));
                await dismissSplashAfterVersionCheck(versionCheckPromise);
              }
            }
          } else {
            const MIN_SPLASH_DURATION = 1500;
            const splashTimeout = parseInt(process.env.VUE_APP_SPLASH_SCREEN_TIMEOUT || '1500', 10);
            const delay = Math.max(MIN_SPLASH_DURATION, splashTimeout);

            setTimeout(async () => {
              const excludedPaths = ['/', '/pre-sign-in', '/login', '/pre-loading'];
              const currentPath = route.path;

              let targetPath = '/login';

              if (isNative) {
                if (currentPath === '/pre-sign-in') {
                  targetPath = preserveRouteOrFallback(route, currentPath);
                } else if (currentPath === '/') {
                  targetPath = '/';
                } else if (currentPath && !excludedPaths.includes(currentPath)) {
                  targetPath = preserveRouteOrFallback(route, currentPath);
                } else {
                  targetPath = '/';
                }
              } else {
                if (currentPath && !excludedPaths.includes(currentPath)) {
                  targetPath = preserveRouteOrFallback(route, currentPath);
                }
              }

              await router.push(targetPath);
              await nextTick();
              await new Promise(resolve => setTimeout(resolve, 50));
              await dismissSplashAfterVersionCheck();
            }, delay);
          }
        } catch (error) {
          console.error('[App] Initialization error:', error);
          isInitializing.value = false;
        }
      })();

      setupCrispCloseListener();
      setupCrispHideOnLoad();
    });

    const handleTimeoutRetry = async () => {
      console.log('[App] Retrying initialization after timeout...');
      showTimeoutModal.value = false;
      isInitializing.value = false;

      showSplashScreen.value = false;
      await nextTick();
      splashScreenKey.value += 1;
      showSplashScreen.value = true;

      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 200));

      const retryStartTime = Date.now();
      const MIN_SPLASH_DURATION = 1500;

      const storeData = checkStoreData(store);
      const { hasMiddleNavIcons, hasFeaturedCards, hasAnimations, hasOnboardStatus } = storeData;
      // Start version check in parallel with retry API calls
      const retryVersionCheckPromise = performVersionCheck();

      if (hasMiddleNavIcons && hasFeaturedCards && hasAnimations && hasOnboardStatus) {
        const elapsedTime = Date.now() - retryStartTime;
        const remainingTime = MIN_SPLASH_DURATION - elapsedTime;
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        const fallbackTarget = determineTargetPath(
          true,
          store,
          route,
          ONBOARDING_STATUS.STAGE_3_LIFESTAGE_COMPLETED,
        );
        const targetDecision = getPostGateNavigationTarget(fallbackTarget);
        if (
          targetDecision.shouldConsumePendingDeepLink &&
          !(await canNavigateAfterForceUpdateCheck('retry cached deep link'))
        ) {
          return;
        }
        await completePostGateNavigation(
          navigateAfterAuthenticatedBootstrap(targetDecision.target),
          targetDecision.shouldConsumePendingDeepLink,
        );
        await nextTick();
        await new Promise(resolve => setTimeout(resolve, 50));
        await dismissSplashAfterVersionCheck(retryVersionCheckPromise);
        return;
      }

      try {
        isInitializing.value = true;

        // In local mode, skip Auth0 readiness check (stub is always ready)
        if (!_isLocalMode) {
          const authInitStartTime = Date.now();
          let authReady = false;
          const authCheckTimeout = setTimeout(() => {
            const authInitDuration = Date.now() - authInitStartTime;
            console.warn(
              `[App] Auth0 initialization timeout (3s) - proceeding anyway. Duration: ${authInitDuration}ms`,
            );
            authReady = true;
          }, 3000);

          while (!authReady && (!auth0 || typeof auth0.isAuthenticated === 'undefined')) {
            await new Promise(resolve => setTimeout(resolve, 100));
            if (auth0 && typeof auth0.isAuthenticated !== 'undefined') {
              authReady = true;
            }
          }
          clearTimeout(authCheckTimeout);
          const authInitDuration = Date.now() - authInitStartTime;
          if (authInitDuration > 0) {
            console.log(`[App] Auth0 initialization completed in ${authInitDuration}ms`);
          }
        }

        if (isAuthenticated.value) {
          const initStartTime = Date.now();
          let retryTimeoutId = null;
          const timeoutPromise = new Promise(resolve => {
            retryTimeoutId = setTimeout(() => {
              const initDuration = Date.now() - initStartTime;
              console.warn(
                `[App] Retry initialization timeout (${
                  INITIALIZATION_TIMEOUT / 1000
                }s) - showing timeout modal again. Duration: ${initDuration}ms`,
              );
              resolve({ timeout: true, onboardStatusSuccess: false });
            }, INITIALIZATION_TIMEOUT);
          });

          const initPromise = (async () => {
            let onboardStatusSuccess = false;
            const onboardStartTime = Date.now();
            try {
              await retryWithBackoff(() => store.dispatch('profile/getOnboardStatus'), 2, 1000);
              console.log(`[App] getOnboardStatus completed in ${Date.now() - onboardStartTime}ms`);
              onboardStatusSuccess = true;
            } catch (onboardError) {
              if (onboardError.response?.status === 401) {
                throw onboardError;
              }
              console.warn(
                `[App] getOnboardStatus failed after ${
                  Date.now() - onboardStartTime
                }ms, proceeding assuming user is registered:`,
                {
                  error: onboardError.message,
                  status: onboardError.response?.status,
                  statusText: onboardError.response?.statusText,
                },
              );
            }

            const actionNames = HOMEPAGE_ACTION_NAMES;
            const dispatchFunctions = [
              () => store.dispatch('homepage/getMiddleNavIcons'),
              () => store.dispatch('homepage/getFeaturedCards'),
              () => store.dispatch('updates/getUpdates', 'all'),
              () => store.dispatch('homepage/getAllAnimations'),
            ];

            const apiCallStartTimes = actionNames.map(() => Date.now());
            const dataLoadResults = await Promise.allSettled(
              dispatchFunctions.map((fn, i) => withTimeout(fn(), API_CALL_TIMEOUT, actionNames[i])),
            );

            const { has401Error } = processApiResults(
              dataLoadResults,
              actionNames,
              apiCallStartTimes,
              store,
              false,
            );

            if (has401Error) {
              throw new Error('Unauthorized - one or more API calls failed with 401');
            }

            const criticalCheck = verifyCriticalApis(dataLoadResults, CRITICAL_API_INDICES);
            if (criticalCheck.shouldThrow) {
              const criticalResults = CRITICAL_API_INDICES.map(idx => ({
                index: idx,
                actionName: actionNames[idx],
                status: dataLoadResults[idx]?.status || 'unknown',
                error: dataLoadResults[idx]?.reason?.message || dataLoadResults[idx]?.reason,
              }));
              console.warn('[App] Critical APIs failed on retry:', criticalResults);
              throw criticalCheck.error;
            }

            if (criticalCheck.criticalSuccessCount < 2) {
              console.warn(
                `[App] Only ${criticalCheck.criticalSuccessCount}/3 critical APIs succeeded, waiting for more...`,
              );
              await new Promise(resolve => setTimeout(resolve, 500));
            }

            const { hasMiddleNavIcons, hasFeaturedCards, hasAnimations } = checkStoreData(store);
            const readyCount = [hasMiddleNavIcons, hasFeaturedCards, hasAnimations].filter(
              Boolean,
            ).length;

            if (readyCount < 2) {
              const storeCheckStartTime = Date.now();
              const maxStoreCheckTime = 500;
              let storeDataReady = false;

              while (!storeDataReady && Date.now() - storeCheckStartTime < maxStoreCheckTime) {
                const checkMiddleNavIcons =
                  store.state.homepage?.middleNavIcons &&
                  Array.isArray(store.state.homepage.middleNavIcons) &&
                  store.state.homepage.middleNavIcons.length > 0;
                const checkFeaturedCards =
                  store.state.homepage?.featuredCards &&
                  Array.isArray(store.state.homepage.featuredCards) &&
                  store.state.homepage.featuredCards.length > 0;
                const checkAnimations =
                  store.state.homepage?.allAnimations &&
                  Array.isArray(store.state.homepage.allAnimations) &&
                  store.state.homepage.allAnimations.length > 0;

                const checkReadyCount = [
                  checkMiddleNavIcons,
                  checkFeaturedCards,
                  checkAnimations,
                ].filter(Boolean).length;

                if (checkReadyCount >= 2) {
                  storeDataReady = true;
                  break;
                }

                await new Promise(resolve => setTimeout(resolve, 100));
              }

              if (!storeDataReady) {
                console.warn('[App] Store data not fully ready, but proceeding with redirect');
              }
            }

            return { timeout: false, onboardStatusSuccess };
          })();

          try {
            const result = await Promise.race([initPromise, timeoutPromise]);

            if (retryTimeoutId && !result.timeout) {
              clearTimeout(retryTimeoutId);
            }

            if (result.timeout) {
              const retryTimeoutDuration = Date.now() - initStartTime;
              console.warn('[App] ⚠️ TIMEOUT MODAL TRIGGERED - Retry initialization timeout', {
                duration: `${retryTimeoutDuration}ms`,
                timeoutLimit: `${INITIALIZATION_TIMEOUT}ms`,
                reason: 'Retry initialization exceeded timeout limit',
                attempt: 'retry',
                timestamp: new Date().toISOString(),
              });
              if (disableTimeoutReloadForLocal) {
                isInitializing.value = false;
                await continueWithoutTimeoutModal('retry-initialization-timeout');
                return;
              }
              showTimeoutModal.value = true;
              return;
            }

            const { onboardStatusSuccess } = result;
            const retryDuration = Date.now() - initStartTime;
            console.log(`[App] Initialization completed successfully in ${retryDuration}ms`);

            // Ensure splash animation fully completes before dismissing
            const MIN_SPLASH_DURATION = 1500;
            const remainingTime = MIN_SPLASH_DURATION - retryDuration;
            if (remainingTime > 0) {
              await new Promise(resolve => setTimeout(resolve, remainingTime));
            }

            const fallbackTarget = determineTargetPath(
              onboardStatusSuccess,
              store,
              route,
              ONBOARDING_STATUS.STAGE_3_LIFESTAGE_COMPLETED,
            );
            const targetDecision = getPostGateNavigationTarget(
              fallbackTarget,
              onboardStatusSuccess,
            );
            if (
              targetDecision.shouldConsumePendingDeepLink &&
              !(await canNavigateAfterForceUpdateCheck('retry post-bootstrap deep link'))
            ) {
              return;
            }
            await completePostGateNavigation(
              navigateAfterAuthenticatedBootstrap(targetDecision.target),
              targetDecision.shouldConsumePendingDeepLink,
            );
            await nextTick();
            await new Promise(resolve => setTimeout(resolve, 50));
            await dismissSplashAfterVersionCheck(retryVersionCheckPromise);
          } catch (error) {
            console.error('[App] Retry initialization error:', {
              error: error.message,
              status: error.response?.status,
            });
            if (error.response && error.response.status === 401) {
              await dismissSplashAfterVersionCheck(retryVersionCheckPromise);
              const { clearAnalyticsSessions } = await import('@/utils/clear-analytics-sessions');
              await clearAnalyticsSessions({ preserveAnalyticsIdentity: true });
              await logout({
                logoutParams: {
                  returnTo: createLogoutUri(
                    process.env.VUE_APP_PACKAGE_ID,
                    process.env.VUE_APP_AUTH0_DOMAIN,
                  ),
                },
                openUrl: async url =>
                  await Browser.open({
                    url,
                    windowName: '_self',
                  }),
              });
            } else if (error.message && error.message.includes('No critical APIs succeeded')) {
              const retryErrorDuration = Date.now() - initStartTime;
              const storeData = checkStoreData(store);
              console.warn(
                '[App] ⚠️ TIMEOUT MODAL TRIGGERED - No critical APIs succeeded on retry',
                {
                  duration: `${retryErrorDuration}ms`,
                  reason:
                    'All critical APIs failed on retry (getMiddleNavIcons, getFeaturedCards, getAllAnimations)',
                  attempt: 'retry',
                  storeData: {
                    hasMiddleNavIcons: storeData.hasMiddleNavIcons,
                    hasFeaturedCards: storeData.hasFeaturedCards,
                    hasAnimations: storeData.hasAnimations,
                    hasOnboardStatus: storeData.hasOnboardStatus,
                  },
                  timestamp: new Date().toISOString(),
                },
              );
              if (disableTimeoutReloadForLocal) {
                isInitializing.value = false;
                await continueWithoutTimeoutModal('retry-no-critical-apis');
                return;
              }
              showTimeoutModal.value = true;
              isInitializing.value = false;
              return;
            } else {
              const targetPath = preserveRouteOrFallback(route, '/tabs/home');
              await navigateAfterAuthenticatedBootstrap(targetPath);
              await nextTick();
              await new Promise(resolve => setTimeout(resolve, 50));
              await dismissSplashAfterVersionCheck(retryVersionCheckPromise);
            }
          }
        } else {
          const MIN_SPLASH_DURATION = 1500;
          const splashTimeout = parseInt(process.env.VUE_APP_SPLASH_SCREEN_TIMEOUT || '1500', 10);
          const delay = Math.max(MIN_SPLASH_DURATION, splashTimeout);

          console.log(
            `[App] User not authenticated (retry) - showing splash screen for ${delay}ms, then navigating`,
          );

          setTimeout(async () => {
            const excludedPaths = ['/', '/pre-sign-in', '/login', '/pre-loading'];
            const currentPath = route.path;

            let targetPath = '/login';
            if (currentPath && !excludedPaths.includes(currentPath)) {
              targetPath = preserveRouteOrFallback(route, currentPath);
            } else if (isNative && currentPath === '/') {
              targetPath = '/';
            }

            await router.push(targetPath);
            await nextTick();
            await new Promise(resolve => setTimeout(resolve, 50));
            await dismissSplashAfterVersionCheck();
          }, delay);
        }
      } finally {
        isInitializing.value = false;
      }
    };

    const showForceUpdateModal = ref(false);
    const forceUpdateMessage = ref('');
    const forceUpdateStoreUrl = ref('');
    const forceUpdateMinimumVersion = ref('');
    const {
      checkForUpdate,
      openStore: _openStore,
      resetState: _resetForceUpdateState,
    } = useVersionCheck();
    let activeVersionCheckPromise = null;

    const handleForceUpdate = async ({ storeUrl }) => {
      try {
        await Browser.open({ url: storeUrl });
      } catch (error) {
        console.error('[App] Error opening store:', error);
        if (typeof window !== 'undefined' && storeUrl) {
          window.location.href = storeUrl;
        }
      }
    };

    const performVersionCheck = async () => {
      if (activeVersionCheckPromise) {
        console.log('[App] Reusing in-flight force update check');
        return activeVersionCheckPromise;
      }

      activeVersionCheckPromise = (async () => {
        try {
          console.log('[App] Performing force update version check...');
          const result = await checkForUpdate();

          if (result.success && result.updateRequired) {
            console.log('[App] Update required, showing modal and keeping splash screen visible');
            forceUpdateMessage.value = result.message || '';
            forceUpdateStoreUrl.value = result.storeUrl || '';
            forceUpdateMinimumVersion.value = result.minimumVersion || '';
            showForceUpdateModal.value = true;
            return true;
          }
          console.log('[App] No update required, navigation can continue');
          return false;
        } catch (error) {
          console.warn('[App] Version check failed, allowing app to continue:', error);
          return false;
        } finally {
          activeVersionCheckPromise = null;
        }
      })();

      return activeVersionCheckPromise;
    };

    const canNavigateAfterForceUpdateCheck = async source => {
      const needsUpdate = await performVersionCheck();
      if (needsUpdate) {
        console.log(`[App] Force update required, blocking ${source} navigation`);
        return false;
      }
      return true;
    };

    const dismissSplashAfterVersionCheck = async (existingPromise = null) => {
      // Concurrent, not sequential: a forced-update check and an OTA download
      // have nothing to say to each other, and serialising them would add the
      // gate's wait to every launch that also checks the version.
      const [needsUpdate] = await Promise.all([
        existingPromise ?? performVersionCheck(),
        otaGate.run(),
      ]);

      if (!needsUpdate) {
        dismissSplashScreen();
      }
    };

    watch(
      () => isAuthenticated.value,
      (isAuth, wasAuth) => {
        if (wasAuth && !isAuth) {
          console.log('[App] User logged out, resetting splash screen for next session');
          splashManuallyDismissed.value = false;
        }
      },
    );

    return {
      showSplashScreen,
      otaGate,
      otaSignIn,
      dismissSplashScreen,
      showTimeoutModal,
      handleTimeoutRetry,
      splashScreenKey,
      showForceUpdateModal,
      forceUpdateMessage,
      forceUpdateStoreUrl,
      forceUpdateMinimumVersion,
      handleForceUpdate,
    };
  },
});
</script>

<style>
.splash-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background-color: #fec629;
}

.splash-fade-leave-active {
  transition: opacity 1300ms ease-out;
}

.router-hidden {
  opacity: 0;
}

/* Ionic sets opacity:0 on pages after swipe-back but doesn't disable touch events,
   causing invisible pages to block the tab bar and other fixed elements */
.ion-page-invisible {
  pointer-events: none !important;
}
</style>
